/**
 *
 * VideoPlayer
 *
 */

import React from 'react';
import PropTypes from 'prop-types';

import throttle from 'helpers/throttle';
import cloneDeep from 'helpers/cloneDeep';
import iOsDetect from 'helpers/iOsDetect';

import Loader from 'components/Loader';

import VideoPlayerControls from './VideoPlayerControls';

import VideoPlayerWrapper from './styles/VideoPlayerWrapper';
import VideoPlayerHolder from './styles/VideoPlayerHolder';
import VideoPlayerStreamOffline from './styles/VideoPlayerStreamOffline';

const defaultVideo = {
  src: 'https://acara-assets-public.nyc3.cdn.digitaloceanspaces.com/videoPlaceholder.mp4'
};

const initialState = {
  canPlay: null,
  currentTime: 0,
  duration: null,
  isFullscreen: false,
  isMouseOver: false,
  paused: true,
  videoLoaded: false,
  videoSources: null,
  sourceString: '',
  sourceMuxString: '',
  hlsVideo: '',
  url: '',
  isStreamLive: true
};

class VideoPlayer extends React.PureComponent {
  constructor(props) {
    super();

    this._isMounted = false;
    this.isIos = iOsDetect();
    this.thisInterval;

    this.autoPlay = this.autoPlay.bind(this);
    this.handleChangeProgress = this.handleChangeProgress.bind(this);
    this.handleReadyStateChange = this.handleReadyStateChange.bind(this);
    this.handleLoadedData = this.handleLoadedData.bind(this);
    this.handleMouseEnter = this.handleMouseEnter.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.handleVolumeChange = this.handleVolumeChange.bind(this);
    this.pause = this.pause.bind(this);
    this.play = this.play.bind(this);
    this.setDuration = this.setDuration.bind(this);
    this.toggleFullscreen = this.toggleFullscreen.bind(this);
    this.checkIfStreaming = this.checkIfStreaming.bind(this);
    this.handleFullScreen = this.handleFullScreen.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handlePictureInPicture = this.handlePictureInPicture.bind(this);
    this.handleLeavePictureInPicture = this.handleLeavePictureInPicture.bind(this);

    this.state = {
      ...cloneDeep(initialState),
      muted: props.muted,
    };

    this.playPromise = null;
  }

  handleLeavePictureInPicture() {
    if (!this.video.paused) {
      setTimeout(() => {
        this.play();
      });
    }
  }


  handlePictureInPicture() {
    if (this.video && this._isMounted)  {
      if (document.pictureInPictureElement) document.exitPictureInPicture();
      this.video.requestPictureInPicture();  
    }
  }

  setVideoFallback() {
    const { sources } = this.props;

    const videoSource = sources ? sources : defaultVideo;
    const cloneInitialState = cloneDeep(initialState);
    this.setState({
      ...cloneInitialState,
      muted: this.props.muted,
      videoSources: <source src={videoSource.src} type={'video/mp4'} />,
      sourceString: videoSource.src
    });
  }

  checkDurationForLiveStream() {
    this.thisInterval = setInterval(() => {

      this.setDuration();
    }, 1000);

  }

  checkIfStreaming = throttle(() => {
      if (this._isMounted) {
        const {
          hlsVideo,
          url
        } = this.state;

        if (hlsVideo && url) {
          hlsVideo.loadSource(url);
        }        
      }  
  }, 1000)

  async setMuxVideo() {

    if (!this._isMounted) return;

    const { sources, isLive } = this.props;

    const url = `https://stream.mux.com/${sources.muxPlaybackId}.m3u8`;
    
    this.setState({
      sourceMuxString: sources.muxPlaybackId
    });

    const importHls = await import('hls.js');

    const Hls = importHls.default

    if (Hls.isSupported()) {
      const hlsVideo = new Hls();
      hlsVideo.loadSource(url);
      hlsVideo.attachMedia(this.video);

      this.setState({
        hlsVideo: hlsVideo,
        url: url
      });
      


      if (isLive) this.checkDurationForLiveStream();
    
      hlsVideo.on(Hls.Events.ERROR, (event, data) => {
        const errorDetails = data.details;
        if (errorDetails === 'manifestLoadError' || data.mediaError === 'mediaError') {
          if (!this._isMounted) return;
          this.setState({
            isStreamLive: false
          });
          window.setTimeout(this.checkIfStreaming, 5000)     
        }
      });

      hlsVideo.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
        if (!this._isMounted) return;
        this.setState({
          isStreamLive: true
        });
      });

    }


  }

  autoPlay() {

    if (!this._isMounted) return;

    const {
      autoplay
    } = this.props;

   

    const { paused } = this.state;

    if (!paused) return;

    const tryAutoplay = () => {
      if (this && this.video && this.video.paused) {

        const playPromise = this.video.play();   
        if (playPromise !== undefined) {
          playPromise.then(() => {
            if (!this._isMounted) return;
            this.setState({ paused: false }, () => {
              try {
                this.handleVolumeChange(1);
              } catch(error) {
                console.log(error);
                this.setState({ paused: true });                
              }
            });
          }).catch((error) => {
            console.log(error);
            setTimeout(tryAutoplay, 1000);
          });
        }
      }      
    }
    if (autoplay) tryAutoplay();   
    
  }

  pause() {

    if (!this._isMounted) return;

    if (this.playPromise) {
      this.playPromise.then(() => this.video.pause());
    } else {
      this.video.pause();
    }

    if (this.mouseTimeout) clearTimeout(this.mouseTimeout);

    this.setState({ paused: true });
  }

  play() {

    if (!this._isMounted) return;

    if (this.video.paused) {
      this.playPromise = this.video.play();

      const {
        duration
      } = this.state;

      const {
        isLive
      } = this.props;

      if (isLive && duration) {
        const currentDuration = duration / 1000;
        this.video.currentTime = currentDuration > 4 ? currentDuration - 4 : currentDuration;
      }

      this.setState({ paused: false }, () => {
        if (this.mouseTimeout) clearTimeout(this.mouseTimeout);
        this.mouseTimeout = setTimeout(() => {
          this.setState({
            isMouseOver: false
          });
        }, 3000);
      });
    }
  }

  handleChangeProgress(elapsed) {
    if (!this._isMounted || !elapsed || typeof elapsed !== 'number') return;
    this.setState({ currentTime: elapsed });
    this.video.currentTime = elapsed / 1000;
  }

  setDuration() {

    if (!this._isMounted) return;
    if (!this.video) return;

    const { duration } = this.video;
    this.setState({ duration: duration * 1000 });
  }

  handleReadyStateChange() {
    const { readyState } = this.video;

    const canPlay = readyState > 2;

    if (this.state.canPlay !== canPlay) {
      this.setState({ canPlay });
    }
  }

  handleLoadedData() {
    if (!this._isMounted) return;
    this.setState({ videoLoaded: true });
    this.autoPlay();
  }

  handleTimeUpdate = throttle(() => {
    if (!this._isMounted) return;
    if (this.video && this.video.currentTime) {
      const { currentTime } = this.video;
      const elapsed = currentTime * 1000;
  
      this.setState({ currentTime: elapsed });
    }
  }, 100);

  // Mouse

  handleMouseMove = throttle(() => {

    if (!this._isMounted) return;


      if (this.mouseTimeout) clearTimeout(this.mouseTimeout);

      if (!this.state.paused && !this.state.mouseOutside) this.setState({ isMouseOver: true }, () => {

        this.mouseTimeout = setTimeout(() => {  
          if (this._isMounted) this.setState({
            isMouseOver: false
          });
        }, 3000);

      });

  }, 300);


  handleMouseEnter() {
    if (!this._isMounted) return;
    if (this.mouseTimeout) clearTimeout(this.mouseTimeout);
    this.setState({ isMouseOver: true, mouseOutside: false });
  }

  handleMouseLeave() {
    if (!this._isMounted) return;
    if (this.mouseTimeout) clearTimeout(this.mouseTimeout);
    this.setState({ isMouseOver: false, mouseOutside: true });
  }

  // Volume

  handleVolumeChange(volume) {
    if (!this._isMounted) return;
    try {
      this.setState({
        volume: volume,
        muted: volume <= 0
      }, () => {
        if (this.video) this.video.volume = volume;
      })
    } catch(error) {
      console.log(error)
    }
  }

  handleFullScreen(event) {

    if (!this._isMounted) return;
    
    if (!document.fullscreenElement) this.setState({ isFullscreen: false }, () => {
      if (document.fullscreenElement && document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    });

  }


  toggleFullscreen() {

    if (!this._isMounted) return;

    const { isFullscreen } = this.state;

    if (!this.videoContainer) return;

    if (isFullscreen) {
      this.setState({ isFullscreen: false }, () => {
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        } else if (document.mozCancelFullScreen) {
          document.mozCancelFullScreen();
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen();
        }
      });
    } else {
      this.setState({ isFullscreen: true }, () => {
        if (this.videoContainer.requestFullscreen) return this.videoContainer.requestFullscreen();
        if (this.videoContainer.webkitRequestFullScreen) return this.videoContainer.webkitRequestFullScreen();
        if (this.videoContainer.webkitEnterFullScreen) return this.videoContainer.webkitEnterFullScreen();
      });
    }
  }

  componentDidMount() {
    this._isMounted = true;
    const { sources, forceRawSource, isBackground } = this.props;


    if (this.video) {
      this.video.addEventListener('leavepictureinpicture', this.handleLeavePictureInPicture);
    }

    if (sources && sources.muxPlaybackId && !this.isIos && !forceRawSource) {
      return this.setMuxVideo();
    }
     
    if (!this.isIos) {
      this.setVideoFallback();
    }

    if (!isBackground) window.addEventListener('fullscreenchange', this.handleFullScreen);
    
  }

  componentWillUnmount() {
    this._isMounted = false;
    if (this.thisInterval) clearInterval(this.thisInterval);
    if (!this.props.isBackground) window.removeEventListener('fullscreenchange', this.handleFullScreen);

    if (this.video) {
      this.video.removeEventListener('leavepictureinpicture', this.handleLeavePictureInPicture);
    }
  }
  



  render() {
    // eslint-disable-next-line no-unused-vars
    const {
      autoplay,
      controls,
      isBackground,
      isLive,
      isResponsive,
      loop,
      sources,
      thisStyles,
      forceRawSource,
      backgroundColor,
      hideLoader
    } = this.props;

    const {
      canPlay,
      currentTime,
      duration,
      isFullscreen,
      isMouseOver,
      paused,
      videoLoaded,
      videoSources,
      volume,
      isStreamLive,
      muted,
    } = this.state;


    if (this.isIos) {

      let videoSourceIos = sources ? sources : defaultVideo;

      if (videoSourceIos && videoSourceIos.muxPlaybackId) {
        videoSourceIos = `https://stream.mux.com/${videoSourceIos.muxPlaybackId}.m3u8`;
      } else if (videoSourceIos) {
        videoSourceIos = videoSourceIos.src;
      }

      if (!videoSourceIos) return '';

      return  (
        <VideoPlayerWrapper
          isBackground={isBackground}
          onMouseEnter={this.handleMouseEnter}
          onMouseMove={this.handleMouseMove}
          onTouchMove={this.handleMouseMove}
          onMouseLeave={this.handleMouseLeave}
          ref={node => (this.videoContainer = node)}
          thisStyles={thisStyles}
          backgroundColor={backgroundColor}
        >
  
          <VideoPlayerHolder
            controls={isBackground ? false : true}
            isBackground={isBackground}
            isResponsive={isResponsive}
            muted={muted || isBackground}
            loop={loop}
            autoPlay={isBackground}
            onLoadedMetadata={this.setDuration}
            onCanPlay={this.handleReadyStateChange}
            onCanPlayThrough={this.handleReadyStateChange}
            onStalled={this.handleReadyStateChange}
            onSuspend={this.handleReadyStateChange}
            onWaiting={this.handleReadyStateChange}
            onLoadedData={this.handleLoadedData}
            onTimeUpdate={this.handleTimeUpdate}
            preload={'auto'}
            ref={node => {
              this.video = node;
            }}
            playsInline
          >
            <source src={videoSourceIos} type={'video/mp4'} />
          </VideoPlayerHolder>
        </VideoPlayerWrapper>
      );
    }

    return (
      <VideoPlayerWrapper
        isBackground={isBackground}
        onMouseEnter={this.handleMouseEnter}
        onMouseMove={this.handleMouseMove}
        onTouchMove={this.handleMouseMove}
        onMouseLeave={this.handleMouseLeave}
        ref={node => (this.videoContainer = node)}
        thisStyles={thisStyles}
        backgroundColor={backgroundColor}
      >

        {
          isStreamLive ? '' : (
            <VideoPlayerStreamOffline>
              Stream is offline. Please wait...
            </VideoPlayerStreamOffline>
          )
        }
        {!canPlay && !hideLoader? (
          <Loader
            thisStyles={{
              position: 'absolute',
              display: 'flex',
              width: '100%',
              height: '100%',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: '1'
            }}
            thisStylesIcon={
              {
                height: 'auto'
              }
            }
          />
        ) : ''}


        {
          videoSources ? (
            <VideoPlayerHolder
              controls={false}
              isBackground={isBackground}
              isResponsive={isResponsive}
              muted={isBackground ? true : muted}
              loop={loop}
              onLoadedMetadata={this.setDuration}
              onCanPlay={this.handleReadyStateChange}
              onCanPlayThrough={this.handleReadyStateChange}
              onStalled={this.handleReadyStateChange}
              onSuspend={this.handleReadyStateChange}
              onWaiting={this.handleReadyStateChange}
              onLoadedData={this.handleLoadedData}
              onTimeUpdate={this.handleTimeUpdate}
              preload={'auto'}
              ref={node => {
                this.video = node;
              }}
          >
            {videoSources}
          </VideoPlayerHolder>
          ) : (
            <VideoPlayerHolder
              controls={false}
              isBackground={isBackground}
              isResponsive={isResponsive}
              muted={isBackground ? true : muted}
              loop={loop}
              onLoadedMetadata={this.setDuration}
              onCanPlay={this.handleReadyStateChange}
              onCanPlayThrough={this.handleReadyStateChange}
              onStalled={this.handleReadyStateChange}
              onSuspend={this.handleReadyStateChange}
              onWaiting={this.handleReadyStateChange}
              onLoadedData={this.handleLoadedData}
              onTimeUpdate={this.handleTimeUpdate}
              preload={'auto'}
              ref={node => {
                this.video = node;
            }}
          />
          )
        }



        {videoLoaded ? (
          <VideoPlayerControls
            controls={controls}
            currentTime={currentTime}
            isFullscreen={isFullscreen}
            duration={duration}
            isLive={isLive}
            onChangeProgress={this.handleChangeProgress}
            onFullscreenChange={this.toggleFullscreen}
            onPlay={this.play}
            onPause={this.pause}
            onVolumeChange={this.handleVolumeChange}
            paused={this.video.paused || paused}
            visible={isMouseOver}
            volume={muted ? 0 : this.video.volume}
            handlePictureInPicture={this.handlePictureInPicture}
          />
        ) : (
          ''
        )}
      </VideoPlayerWrapper>
    );
  }
}

VideoPlayer.propTypes = {
  autoplay: PropTypes.bool,
  controls: PropTypes.bool,
  isBackground: PropTypes.bool,
  isLive: PropTypes.bool,
  isResponsive: PropTypes.bool,
  muted: PropTypes.bool,
  sources: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object
  ]),
};

export default VideoPlayer;
